import { FIELD_GPS_MAX_FIX_AGE_MS, FIELD_GPS_SAMPLE_INTERVAL_MS, type GpsGapKind } from '@groupe-reca/contracts'
import type { GeoFailure, GeoFix, GeolocationPort } from '@/platform/geolocation'
import type { LocalStore } from '@/platform/localStore'
import { bearingDegrees, distanceMeters, smoothHeading } from './geo'
import type { PositionSync } from './PositionSync'

export type GpsStatus = 'idle' | 'pending' | 'ok' | 'stale' | 'denied' | 'unavailable' | 'unsupported'

export interface TrackerState {
  status: GpsStatus
  /** Dernière mesure reçue (affichage carte), même si elle n'a pas encore été enregistrée. */
  position: { lat: number; lng: number } | null
  heading: number | null
  lastFixAt: number | null
  lastRecordedAt: number | null
  /** Précision (m) et vitesse (m/s) du dernier relevé, telles que fournies par l'appareil (affichage seulement). */
  accuracy?: number | null
  speed?: number | null
}

export interface TrackerClock {
  now: () => number
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
  uuid: () => string
}

const browserClock: TrackerClock = {
  now: () => Date.now(),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (handle) => {
    clearInterval(handle as ReturnType<typeof setInterval>)
  },
  uuid: () => crypto.randomUUID(),
}

/** Une mesure n'est enregistrée qu'une fois, et seulement si elle est récente : jamais une vieille position redatée. */
export function isRecordable(fix: GeoFix, now: number, lastRecordedFix: number | null): boolean {
  if (now - fix.timestamp > FIELD_GPS_MAX_FIX_AGE_MS || fix.timestamp > now + 60_000) return false
  return lastRecordedFix === null || fix.timestamp > lastRecordedFix
}

/**
 * Relevé de position toutes les 30 s pendant une mission en cours, application au premier plan. Arrêté en pause,
 * à la fin et quand la page est masquée (le navigateur ne garantit rien en arrière-plan); chaque interruption est
 * consignée pour que la trace ne la relie pas. Une permission refusée n'est jamais redemandée automatiquement.
 */
export class PositionTracker {
  private state: TrackerState = { status: 'idle', position: null, heading: null, lastFixAt: null, lastRecordedAt: null }
  private latest: GeoFix | null = null
  private lastRecordedFix: number | null = null
  private headingAnchor: { lat: number; lng: number } | null = null
  private stopWatch: (() => void) | null = null
  private timer: unknown = null
  private openGap: { id: string; kind: GpsGapKind; startedAt: string } | null = null
  private denied = false
  private listeners = new Set<(state: TrackerState) => void>()

  constructor(
    private readonly missionId: string,
    private readonly geo: GeolocationPort,
    private readonly store: LocalStore,
    private readonly sync: PositionSync,
    private readonly clock: TrackerClock = browserClock,
  ) {}

  subscribe(listener: (state: TrackerState) => void): () => void {
    this.listeners.add(listener)
    listener(this.state)
    return () => this.listeners.delete(listener)
  }

  get snapshot(): TrackerState {
    return this.state
  }

  /** Mission en cours et page visible. */
  start() {
    if (this.timer !== null) return
    if (!this.geo.supported) {
      this.set({ status: 'unsupported' })
      return
    }
    if (this.denied) {
      this.set({ status: 'denied' })
      return
    }
    this.set({ status: this.state.position ? this.state.status : 'pending' })
    this.stopWatch = this.geo.watch(
      (fix) => {
        this.onFix(fix)
      },
      (failure) => {
        this.onFailure(failure)
      },
    )
    this.timer = this.clock.setInterval(() => {
      void this.tick()
    }, FIELD_GPS_SAMPLE_INTERVAL_MS)
  }

  /** Pause, fin de mission ou page masquée (`reason` consigne l'interruption). */
  stop(reason: GpsGapKind | null) {
    if (this.timer !== null) this.clock.clearInterval(this.timer)
    this.timer = null
    this.stopWatch?.()
    this.stopWatch = null
    if (reason) void this.beginGap(reason)
    this.set({ status: this.denied ? 'denied' : 'idle' })
  }

  /** Reprise après une pause ou un retour au premier plan : l'interruption est refermée. */
  resume() {
    void this.endGap()
    this.start()
  }

  /** Nouvel essai explicite demandé par l'opérateur après un refus (jamais automatique). */
  retryPermission() {
    this.denied = false
    this.stop(null)
    this.start()
  }

  private onFix(fix: GeoFix) {
    const position = { lat: fix.latitude, lng: fix.longitude }
    let heading = this.state.heading
    if (fix.heading !== null && (fix.speed ?? 0) >= 0.8) heading = smoothHeading(heading, fix.heading)
    else if (this.headingAnchor && distanceMeters(this.headingAnchor, position) >= 6)
      heading = smoothHeading(heading, bearingDegrees(this.headingAnchor, position))
    if (heading !== this.state.heading || this.headingAnchor === null) this.headingAnchor = position
    const first = this.latest === null
    this.latest = fix
    this.set({ status: 'ok', position, heading, lastFixAt: fix.timestamp, accuracy: fix.accuracy, speed: fix.speed })
    // Premier relevé tout de suite, puis toutes les 30 s.
    if (first) void this.tick()
  }

  private onFailure(failure: GeoFailure) {
    if (failure === 'PERMISSION_DENIED') {
      this.denied = true
      this.stop(null)
      void this.beginGap('PERMISSION_DENIED')
      this.set({ status: 'denied' })
      return
    }
    if (failure === 'UNSUPPORTED') {
      this.set({ status: 'unsupported' })
      return
    }
    void this.beginGap('POSITION_UNAVAILABLE')
    this.set({ status: 'unavailable' })
  }

  async tick() {
    const now = this.clock.now()
    const fix = this.latest
    if (fix && isRecordable(fix, now, this.lastRecordedFix)) {
      this.lastRecordedFix = fix.timestamp
      await this.store.enqueuePosition({
        id: this.clock.uuid(),
        missionId: this.missionId,
        capturedAt: new Date(fix.timestamp).toISOString(),
        latitude: fix.latitude,
        longitude: fix.longitude,
        accuracy: fix.accuracy,
        heading: fix.heading === null ? null : ((fix.heading % 360) + 360) % 360,
        speed: fix.speed,
      })
      await this.endGap()
      this.set({ status: 'ok', lastRecordedAt: now })
    } else if (this.state.status !== 'denied' && this.state.status !== 'unsupported') {
      // Aucune mesure récente : rien n'est enregistré, l'opérateur le voit.
      if (fix) {
        await this.beginGap('STALE_POSITION')
        this.set({ status: 'stale' })
      }
    }
    await this.sync.flush()
  }

  private async beginGap(kind: GpsGapKind) {
    if (this.openGap) return
    this.openGap = { id: this.clock.uuid(), kind, startedAt: new Date(this.clock.now()).toISOString() }
    await this.store.saveGap({ ...this.openGap, missionId: this.missionId, endedAt: null })
  }

  private async endGap() {
    const gap = this.openGap
    if (!gap) return
    this.openGap = null
    await this.store.saveGap({ ...gap, missionId: this.missionId, endedAt: new Date(this.clock.now()).toISOString() })
  }

  private set(patch: Partial<TrackerState>) {
    this.state = { ...this.state, ...patch }
    for (const listener of this.listeners) listener(this.state)
  }
}
